import React, { useEffect, useState } from 'react';
import CobuConstants from '~/helpers/cobuConstants';
import MultiSelect from '~/components/MultiSelect/MultiSelect';
import { SelectType } from '~/enums/SelectType';
import { WordLanguage } from '~/types/manageWords';
import styles from './ManageWordsForm.module.css';

type SelectOption = {
  value: string;
  label: string;
};

const LANGUAGE_OPTIONS: { value: WordLanguage; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' }
];

type ManageWordFormProps = {
  isUpdate?: boolean;
  onPressCancel: () => void;
  term?: string;
  onSubmit: (
    term: string,
    buildingId?: string | null,
    language?: WordLanguage
  ) => void;
  buildingOptions?: SelectOption[];
  initialBuildingId?: string | null;
  initialLanguage?: WordLanguage;
};

export default function ManageWordForm({
  isUpdate = false,
  onPressCancel,
  term = '',
  onSubmit,
  buildingOptions = [],
  initialBuildingId = null,
  initialLanguage = 'en'
}: ManageWordFormProps) {
  const [currentTerm, setCurrentTerm] = useState(term);
  const [selectedLanguage, setSelectedLanguage] = useState<WordLanguage>(
    initialLanguage
  );
  const [selectedBuildingId, setSelectedBuildingId] = useState<string | null>(
    initialBuildingId
  );

  const selectedBuildingValue = selectedBuildingId
    ? buildingOptions.find(option => option.value === selectedBuildingId) || null
    : null;

  const [selectedBuildingMultiValue, setSelectedBuildingMultiValue] = useState<SelectOption[]>(
    selectedBuildingValue ? [selectedBuildingValue] : []
  );

  useEffect(() => {
    if (initialBuildingId) {
      setSelectedBuildingId(initialBuildingId);
      const option = buildingOptions.find(opt => opt.value === initialBuildingId);
      setSelectedBuildingMultiValue(option ? [option] : []);
      return;
    }
    if (buildingOptions.length > 0) {
      setSelectedBuildingId(buildingOptions[0].value);
      setSelectedBuildingMultiValue([buildingOptions[0]]);
      return;
    }
    setSelectedBuildingId(null);
    setSelectedBuildingMultiValue([]);
  }, [initialBuildingId, buildingOptions]);

  return (
    <form onSubmit={handleSubmit}>
      <div className='item'>
        <label htmlFor='title'>Term</label>
        <input
          type='text'
          onChange={(e) => setCurrentTerm(e.target.value)}
          value={currentTerm}
          className={styles.capitalizeInput}
        />
      </div>
      <div className='item'>
        <label htmlFor='languageSelect'>Language</label>
        <select
          id='languageSelect'
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value as WordLanguage)}
        >
          {LANGUAGE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      {buildingOptions.length > 0 && (
        <div className='item'>
          <label htmlFor='buildingSelect'>
            {CobuConstants.manageWords.buildingLabel}
          </label>
          <div style={{ width: '100%' }}>
            <MultiSelect
              options={buildingOptions}
              placeholder="Select building..."
              isSearchable={true}
              selectType={SelectType.Form}
              disabled={false}
              isClearable={false}
              noOptionsMessage="No buildings found"
              isError={null}
              multiValue={selectedBuildingMultiValue}
              setMultiValue={(newValue: SelectOption[]) => {
                setSelectedBuildingMultiValue(newValue);
                if (newValue.length > 0) {
                  setSelectedBuildingId(newValue[0].value);
                } else {
                  setSelectedBuildingId(null);
                }
              }}
              cobuPlusBuildings={[]}
              singleSelect={true}
            />
          </div>
        </div>
      )}
      <div className='formButtons'>
          <button
            className='btn-standard btn-secondary'
            onClick={onPressCancel}
            type='button'
          >
            {CobuConstants.manageWords.cancel}
          </button>
          <button
            className='btn-standard btn-primary margin-left-16 '
            type='submit'
          >
            {isUpdate ? CobuConstants.manageWords.update : CobuConstants.manageWords.create}
          </button>
        </div>
    </form>
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(currentTerm, selectedBuildingId, selectedLanguage);
  }
}
